"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.encodeCollaborationUpdate = encodeCollaborationUpdate;
exports.decodeCollaborationUpdate = decodeCollaborationUpdate;
exports.hashNoteText = hashNoteText;
exports.isCollaborationStateCurrent = isCollaborationStateCurrent;
exports.canMergeCollaborativeNotes = canMergeCollaborativeNotes;
exports.mergeNoteCollaborationStates = mergeNoteCollaborationStates;
exports.collaborativeNoteConflictStrategy = collaborativeNoteConflictStrategy;
const yjs_1 = require("yjs");
const features_1 = require("@standardnotes/features");
const domain_core_1 = require("@standardnotes/domain-core");
const TypeCheck_1 = require("../../Abstract/Item/Interfaces/TypeCheck");
const ConflictStrategy_1 = require("../../Abstract/Item/Types/ConflictStrategy");
function encodeCollaborationUpdate(update) {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < update.length; i += chunkSize) {
        binary += String.fromCharCode(...update.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}
function decodeCollaborationUpdate(state) {
    const binary = atob(state);
    const update = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        update[i] = binary.charCodeAt(i);
    }
    return update;
}
/** cyrb53 - fast, non-cryptographic string hash */
function hashNoteText(text) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < text.length; i++) {
        const ch = text.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}
/** Whether the note's Yjs state still describes its text (the text was not changed by an editor without Yjs) */
function isCollaborationStateCurrent(note) {
    const collaboration = note.myRadmitClaudeCollaboration;
    if (!collaboration || !collaboration.state || !collaboration.origin) {
        return false;
    }
    return collaboration.textHash === hashNoteText(note.text || '');
}
function canMergeCollaborativeNotes(base, apply) {
    var _a, _b;
    return (base.noteType === features_1.NoteType.Super &&
        apply.noteType === features_1.NoteType.Super &&
        isCollaborationStateCurrent(base) &&
        isCollaborationStateCurrent(apply) &&
        ((_a = base.myRadmitClaudeCollaboration) === null || _a === void 0 ? void 0 : _a.origin) === ((_b = apply.myRadmitClaudeCollaboration) === null || _b === void 0 ? void 0 : _b.origin));
}
/**
 * Merges the Yjs states of two versions of the same note. The text of `base` is kept and marked as stale,
 * the editor (or the headless renderer) renders the merged text from the state.
 */
function mergeNoteCollaborationStates(base, apply) {
    if (!base.myRadmitClaudeCollaboration || !apply.myRadmitClaudeCollaboration) {
        throw Error('Cannot merge notes without collaboration state');
    }
    const merged = (0, yjs_1.mergeUpdates)([
        decodeCollaborationUpdate(base.myRadmitClaudeCollaboration.state),
        decodeCollaborationUpdate(apply.myRadmitClaudeCollaboration.state),
    ]);
    return {
        state: encodeCollaborationUpdate(merged),
        origin: base.myRadmitClaudeCollaboration.origin,
        textHash: hashNoteText(base.text || ''),
        textStale: true,
    };
}
/**
 * Conflict strategy of a note: conflicts that would create a conflicted copy of a collaborative Super note
 * are merged instead (ConflictStrategy.MergeCollaborativeNote).
 */
function collaborativeNoteConflictStrategy(base, item, strategy) {
    const wouldDuplicate = strategy === ConflictStrategy_1.ConflictStrategy.KeepBaseDuplicateApply || strategy === ConflictStrategy_1.ConflictStrategy.DuplicateBaseKeepApply;
    if (wouldDuplicate &&
        item.content_type === domain_core_1.ContentType.TYPES.Note &&
        (0, TypeCheck_1.isDecryptedItem)(item) &&
        canMergeCollaborativeNotes(base, item.content)) {
        return ConflictStrategy_1.ConflictStrategy.MergeCollaborativeNote;
    }
    return strategy;
}
